import { useCallback, useEffect, useState } from "react";
import { isInstrumentTypeEnabled } from "../../config/features";
import { IMarket } from "../../contexts/MarketContext";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { jsonParse } from "../../utils/strings";

const WATCHLIST_UPDATED_EVENT = "marginx:watchlist-updated";

const readStoredWatchlist = (): IMarket[] => {
  const localStorageItem = localStorage.getItem(LocalStorageKeyEnum.WATCHLIST);
  if (localStorageItem) {
    try {
      const stored = jsonParse(localStorageItem) as IMarket[];
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      // Do nothing
    }
  }
  return [];
};

/**
 * Entries are `{asset, derivative}` pairs persisted across sessions, so the
 * stored list can name a vertical that has since been switched off. Those are
 * hidden from consumers — but see `updateWatchlist`, which preserves them in
 * storage so they reappear intact if the vertical is re-enabled.
 */
const loadWatchlist = (): IMarket[] =>
  readStoredWatchlist().filter((entry) =>
    isInstrumentTypeEnabled(entry?.derivative)
  );

const useWatchlist = () => {
  const [watchlist, setWatchList] = useState<IMarket[]>(loadWatchlist());

  const refreshWatchlist = useCallback(() => {
    setWatchList(loadWatchlist());
  }, []);

  // Sync across instances and tabs: when any component updates the watchlist,
  // every other consumer re-renders without waiting for a polling tick.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LocalStorageKeyEnum.WATCHLIST) {
        refreshWatchlist();
      }
    };
    const onCustom = () => refreshWatchlist();
    window.addEventListener("storage", onStorage);
    window.addEventListener(WATCHLIST_UPDATED_EVENT, onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(WATCHLIST_UPDATED_EVENT, onCustom);
    };
  }, [refreshWatchlist]);

  const updateWatchlist = useCallback((list: IMarket[]) => {
    setWatchList(list);
    try {
      // `list` only ever contains entries for enabled verticals, so carry the
      // hidden ones through instead of quietly deleting a user's saved markets.
      const hidden = readStoredWatchlist().filter(
        (entry) => !isInstrumentTypeEnabled(entry?.derivative)
      );
      localStorage.setItem(
        LocalStorageKeyEnum.WATCHLIST,
        JSON.stringify([...list, ...hidden])
      );
      // Notify same-tab listeners (storage event fires cross-tab only).
      window.dispatchEvent(new Event(WATCHLIST_UPDATED_EVENT));
    } catch (error) {
      // Do nothing
    }
  }, []);

  return {
    watchlist,
    updateWatchlist,
    refreshWatchlist,
  };
};

export default useWatchlist;
